<template>
  <canvas
    ref="canvasRef"
    :class="[
      'firework-canvas-v2',
      props.isVisible ? 'opacity-100' : 'opacity-0'
    ]"
  />
</template>

<script setup>
import { ref, onMounted, onUnmounted, defineProps, defineEmits, defineExpose } from 'vue'
import { SmallFirework2, LargeFirework2 } from './lib/fireworks2'

const props = defineProps({
  isVisible: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['animationComplete'])

const canvasRef = ref(null)
const animationRef = ref(0)
const smallFireworkRef = ref(new SmallFirework2())
const largeFireworkRef = ref(new LargeFirework2())

// 清除画布
const clearCanvas = () => {
  const canvas = canvasRef.value
  if (!canvas) return
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  ctx.clearRect(0, 0, canvas.width, canvas.height)
}

// 动画循环
const animate = () => {
  const canvas = canvasRef.value
  if (!canvas) return
  const ctx = canvas.getContext('2d')
  if (!ctx) return

  // 使用更柔和的清除方式，创建更自然的拖尾
  ctx.fillStyle = 'rgba(0, 0, 0, 0.1)'
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  let hasActiveAnimation = false

  // 更新小烟花
  if (smallFireworkRef.value.isActive) {
    hasActiveAnimation = smallFireworkRef.value.update(ctx)
  }

  // 更新大烟花
  if (largeFireworkRef.value.isActive) {
    hasActiveAnimation = largeFireworkRef.value.update(ctx) || hasActiveAnimation
  }

  if (hasActiveAnimation) {
    animationRef.value = requestAnimationFrame(animate)
  } else {
    clearCanvas()
    emit('animationComplete')
  }
}

// 播放小烟花
const playSmallFirework = () => {
  const canvas = canvasRef.value
  if (!canvas) return
  const ctx = canvas.getContext('2d')
  if (!ctx) return

  // 随机位置（屏幕中心区域）
  const x = canvas.width * (0.3 + Math.random() * 0.4)
  const y = canvas.height * (0.2 + Math.random() * 0.3)

  smallFireworkRef.value.start(ctx, x, y, () => {
    // 小烟花完成回调
  })
  
  cancelAnimationFrame(animationRef.value)
  animationRef.value = requestAnimationFrame(animate)
}

// 播放大烟花
const playLargeFirework = () => {
  const canvas = canvasRef.value
  if (!canvas) return
  const ctx = canvas.getContext('2d')
  if (!ctx) return

  // 大烟花在屏幕中心偏上
  const x = canvas.width / 2
  const y = canvas.height * 0.3

  largeFireworkRef.value.start(ctx, x, y, () => {
    // 大烟花完成回调
  })
  
  cancelAnimationFrame(animationRef.value)
  animationRef.value = requestAnimationFrame(animate)
}

// 暴露方法给父组件
defineExpose({
  playSmallFirework,
  playLargeFirework
})

// 设置画布尺寸
const resizeCanvas = () => {
  const canvas = canvasRef.value
  if (!canvas) return
  canvas.width = window.innerWidth
  canvas.height = window.innerHeight
}

onMounted(() => {
  resizeCanvas()
  window.addEventListener('resize', resizeCanvas)
})

onUnmounted(() => {
  window.removeEventListener('resize', resizeCanvas)
  cancelAnimationFrame(animationRef.value)
})
</script>

<style scoped>
.firework-canvas-v2 {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 102;
  pointer-events: none;
  transition: opacity 0.3s ease-in-out;
  background: transparent;
}

.opacity-100 {
  opacity: 1;
}

.opacity-0 {
  opacity: 0;
}
</style>